import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { CanaryAIAttribution } from '@hcs/canaryai';
import { CondoIcon, HomeIcon, Skeleton, Toggle } from '@hcs/design-system';
import { useScreenSize } from '@hcs/hooks';
import { useNavigationContext } from '@hcs/navigation';
import { CERBERUS_STATS_FIELDS, MarketInsightsPlace } from '@hcs/types';

import { CERBERUS_STATS_FIELD_CONFIGS } from '../../configs';
import { CERBERUS_STATS_TYPE } from '../../constants';
import { PropertyTypeWarning } from '../../features';
import {
  CerberusStatsDetailsTable,
  CerberusStatsDetailsTableProps,
} from '../CerberusStatsDetailsTable';
import { OpenInMarketInsightsButton } from '../OpenInMarketInsightsButton';

import pdfStyles from '../MarketInsightsPdfStyles.module.css';
import styles from './CerberusStatsSummary.module.css';
const DisplayName =
  CERBERUS_STATS_FIELD_CONFIGS[CERBERUS_STATS_FIELDS.displayName].Display;

export interface CerberusStatsSummaryProps
  extends Pick<
    CerberusStatsDetailsTableProps,
    | 'forPdf'
    | 'blurred'
    | 'dataHcName'
    | 'className'
    | 'cerberusStats'
    | 'fields'
    | 'hideInteractive'
  > {
  hideTitle?: boolean;
  children?: React.ReactNode;
  cerberusStatsIsLoading?: boolean;
  summaryData?: string;
  place?: MarketInsightsPlace;
  summaryDataIsLoading?: boolean;
  propertyType: 'singleFamily' | 'condo';
  setPropertyType?: (type: 'singleFamily' | 'condo') => void;
  showPropertyTypeWarning?: boolean;
}
export const CerberusStatsSummary = ({
  dataHcName,
  className,
  cerberusStats,
  hideInteractive,
  hideTitle,
  children,
  cerberusStatsIsLoading,
  summaryData,
  summaryDataIsLoading,
  forPdf,
  fields,
  place,
  blurred,
  propertyType,
  setPropertyType,
  showPropertyTypeWarning,
}: CerberusStatsSummaryProps) => {
  const { isPhoneWidth } = useScreenSize();
  const { pathname } = useLocation();
  const { marketInsightsLink } = useNavigationContext();

  const isInMarketInsights =
    !!marketInsightsLink && pathname.startsWith(marketInsightsLink);

  // By moving the options into a useMemo, the array reference stays stable
  // across renders, preventing the measurement logic
  // from resetting unnecessarily.
  const toggleOptions = useMemo(
    () => [
      {
        label: (
          <div className={styles.PropertyTypeOptionContainer}>
            <div className={styles.PropertyTypeOptionTitle}>Single family</div>
            <div className={styles.PropertyTypeOptionSubtitle}>
              Detached homes
            </div>
          </div>
        ),
        value: 'singleFamily' as const,
        icon: <HomeIcon size="md" />,
      },
      {
        label: (
          <div className={styles.PropertyTypeOptionContainer}>
            <div className={styles.PropertyTypeOptionTitle}>Condo/Townhome</div>
            <div className={styles.PropertyTypeOptionSubtitle}>
              Attached units
            </div>
          </div>
        ),
        value: 'condo' as const,
        icon: <CondoIcon size="md" />,
      },
    ],
    [],
  );

  return (
    <div
      className={classNames(className, {
        [classNames(pdfStyles.PdfSummary, pdfStyles.FullWidthContainer)]:
          forPdf,
      })}
    >
      {!hideTitle && (
        <div className={styles.Name}>
          {cerberusStatsIsLoading ? (
            <Skeleton type="text" width={100} />
          ) : (
            <>
              <DisplayName cerberusStats={cerberusStats} />{' '}
              {cerberusStats?.type === CERBERUS_STATS_TYPE.Msa ? 'MSA' : ''}
            </>
          )}
        </div>
      )}
      <div
        data-testid="summary-text"
        className={classNames(styles.SummaryText, {
          [styles.SummaryTextMargin]: hideTitle,
        })}
      >
        {summaryDataIsLoading ? (
          <>
            <Skeleton type="text" repeat={6} marginTop={7} />
            <Skeleton type="text" repeat={1} marginTop={10} width={230} />
          </>
        ) : (
          <>
            {summaryData}
            <div className={styles.AttributionRow}>
              {/* Render spacer div if no attribution to prevent layout shift */}
              {summaryData ? <CanaryAIAttribution /> : <div />}
              {!isPhoneWidth && !isInMarketInsights && !forPdf && place && (
                <div className={styles.ColumnButtonSection}>
                  <OpenInMarketInsightsButton
                    place={place}
                    className={styles.ColumnButton}
                    dataHcName="msa-column"
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {setPropertyType && propertyType && !hideInteractive && (
        <div className={styles.PropertyTypeBox}>
          <div className={styles.PropertyTypeLabel}>PROPERTY TYPE</div>
          <div className={styles.PropertyTypeToggleWrapper}>
            <Toggle
              options={toggleOptions}
              value={propertyType}
              onChange={() =>
                setPropertyType(
                  propertyType === 'singleFamily' ? 'condo' : 'singleFamily',
                )
              }
              theme={{
                Toggle: styles.PropertyTypeToggle,
                Option: styles.PropertyTypeToggleOption,
                selected: styles.PropertyTypeToggleSelected,
                SelectedMask: styles.PropertyTypeToggleSelectedMask,
              }}
              equalWidthOptions={!isPhoneWidth}
              dataHcName={`${dataHcName}-property-type-toggle`}
            />
          </div>
        </div>
      )}
      {children}
      {cerberusStatsIsLoading ? (
        <Skeleton type="text" repeat={isPhoneWidth ? 11 : 6} marginTop={7} />
      ) : (
        <CerberusStatsDetailsTable
          hideInteractive={hideInteractive}
          forPdf={forPdf}
          dataHcName={dataHcName}
          cerberusStats={cerberusStats}
          fields={fields}
          blurred={blurred}
        />
      )}
      {showPropertyTypeWarning && propertyType && (
        <PropertyTypeWarning
          dataHcName={`${dataHcName}-property-type-warning`}
          propertyType={propertyType}
          hideInteractive={hideInteractive}
          setPropertyType={setPropertyType}
        />
      )}
    </div>
  );
};
